// 방문자가 내부 트래픽인지 판정한다. 순수 함수만 — I/O 도 부수효과도 없다.
//
// 이 모듈은 "제외한다"는 결정을 하지 않는다. internal 이라는 라벨만 계산하고,
// 실제 제외는 GA4 의 데이터 필터(내부 트래픽, traffic_type = internal)가 한다.
// 그래서 "내부 트래픽도 보고 싶다"가 되면 GA4 에서 필터를 끄면 되고 배포가 필요 없다.

import { UNO } from "@/features/ui/constants";
import type { Identity } from "@/features/ui/hooks/use-identity";

/**
 * GA4 집계에서 제외할 자사·테스트 계정의 회사 id.
 *
 * company.type 으로는 판단하지 않는다 — UNO 는 AGENCY 지만 외부 에이전시 고객사도
 * AGENCY 라서 타입으로 거르면 고객 트래픽까지 사라진다.
 * 회사명("Test" 포함 여부)도 쓰지 않는다 — 고객사가 이름을 그렇게 지으면 같이 빠지고,
 * "Contest"·"Latest" 같은 이름에 오탐하며, 무엇보다 회사명은 JWT 에 없어서
 * /users 응답을 기다려야 한다(그만큼 수집이 늦어지고 로그인 전에는 알 수 없다).
 *
 * 테스트 계정이 늘어나면 이 배열에 회사 id 를 추가한다.
 */
export const INTERNAL_COMPANY_IDS: readonly number[] = [
  UNO.id,
  183,
  196,
  197,
  214,
  215,
  456,
  501,
  3421,
  3558,
  3999,
  3998,
  4002,
  4001,
  4000,
  4007,
  3803,
  3934,
  678,
  2925,
  4011,
  3699,
  3045,
  184,
  185,
  186,
  195,
  1448,
  3204,
];

/** GA4 데이터 필터가 읽는 값. */
export type TrafficType = "internal" | "external";

/** 리포트 세그먼트용 사용자 속성. traffic_type 보다 결이 세분화돼 있다. */
export type AccountClass = "guest" | "pending" | "internal" | "customer";

export function isInternalCompany(companyId?: number | null): boolean {
  return companyId != null && INTERNAL_COMPANY_IDS.includes(companyId);
}

/**
 * 로그인 상태만 보고 판정한다. 게스트는 "external" —
 * 마지막으로 알던 값으로 덮어쓰는 것은 호출자(analytics.ts)의 책임이다.
 */
export function resolveTrafficType(identity: Identity | null): TrafficType {
  return isInternalCompany(identity?.company?.id) ? "internal" : "external";
}

export function resolveAccountClass(identity: Identity | null): AccountClass {
  if (identity === null) {
    return "guest"; // 랜딩·로그인 전
  }
  if (identity.company === null) {
    return "pending"; // 가입 승인 대기
  }
  return isInternalCompany(identity.company.id) ? "internal" : "customer";
}
